import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { apiClient, getErrorMessage } from '../../api/client'
import { useAuthStore } from '../../stores/authStore'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { resolveTenantSlug } from '../../lib/tenant'

type ProviderMinimal = {
  id: number
  full_name: string
  role: 'admin' | 'bcba' | 'rbt' | 'billing' | 'parent'
}

type ClientListItem = {
  id: number
  full_name: string
  medical_record_number: string
  primary_diagnosis_code: string
}

type AuthorizationListItem = {
  id: number
  authorization_number: string
  service_code: string
  start_date: string
  end_date: string
  available_units: string
  status: string
  is_active: boolean
}

const schema = z.object({
  client_id: z.coerce.number().int().positive('Client is required'),
  authorization_id: z.coerce.number().int().positive('Authorization is required'),
  scheduled_start_local: z.string().min(1, 'Start time is required'),
  scheduled_end_local: z.string().min(1, 'End time is required'),
  place_of_service: z.enum(['12', '03', '11', '22', '99', '02']).default('12'),
  modifiers_csv: z.string().optional().default(''),
  diagnosis_code: z.string().min(1, 'Diagnosis code is required'),
  rendering_provider_id: z.coerce.number().int().positive('Rendering provider is required'),
  supervising_provider_id: z.coerce.number().int().optional().nullable(),
  is_billable: z.boolean().default(true),
})

type FormData = z.infer<typeof schema>

function toIsoFromLocalInput(value: string): string {
  const d = new Date(value)
  return d.toISOString()
}

export default function SessionCreatePage() {
  const tenantSlug = resolveTenantSlug()
  const navigate = useNavigate()
  const isOnline = useOnlineStatus()
  const user = useAuthStore((s) => s.user)
  const [searchParams] = useSearchParams()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [clientSearch, setClientSearch] = useState('')

  const defaultClientId = searchParams.get('client')
  const defaultClientIdNum = defaultClientId ? Number(defaultClientId) : undefined

  const canCreate = user?.role === 'admin' || user?.role === 'bcba' || user?.role === 'rbt'

  const providersQuery = useOfflineFirstQuery<{ results: ProviderMinimal[] }>({
    queryKey: ['providers'],
    cacheKey: `tenant:${tenantSlug ?? 'unknown'}:providers:list`,
    cacheMaxAgeMs: 30 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<{ results: ProviderMinimal[] }>('/api/v1/providers/')
      return res.data
    },
    enabled: Boolean(canCreate),
  })

  const clientsQuery = useOfflineFirstQuery<{ results: ClientListItem[] }>({
    queryKey: ['clients', { search: clientSearch }],
    cacheKey: `tenant:${tenantSlug ?? 'unknown'}:clients:pick:q=${clientSearch.trim().toLowerCase() || 'all'}`,
    cacheMaxAgeMs: 10 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<{ results: ClientListItem[] }>('/api/v1/clients/', {
        params: clientSearch.trim() ? { search: clientSearch.trim() } : {},
      })
      return res.data
    },
    enabled: Boolean(canCreate),
  })

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      client_id: defaultClientIdNum ?? undefined,
      authorization_id: undefined,
      scheduled_start_local: '',
      scheduled_end_local: '',
      place_of_service: '12',
      modifiers_csv: '',
      diagnosis_code: '',
      rendering_provider_id: user?.id ?? 0,
      supervising_provider_id: null,
      is_billable: true,
    },
  })

  const clientId = watch('client_id')
  const authorizationId = watch('authorization_id')

  const authorizationsQuery = useOfflineFirstQuery<AuthorizationListItem[]>({
    queryKey: ['client', clientId, 'authorizations'],
    cacheKey: `tenant:${tenantSlug ?? 'unknown'}:client:${clientId}:authorizations`,
    cacheMaxAgeMs: 10 * 60 * 1000,
    enabled: Number.isFinite(clientId) && clientId > 0,
    queryFn: async () => {
      const res = await apiClient.get<AuthorizationListItem[]>(
        `/api/v1/clients/${clientId}/authorizations/`
      )
      return res.data
    },
  })

  const selectedClient = useMemo(() => {
    return (clientsQuery.data?.results ?? []).find((c) => c.id === clientId) ?? null
  }, [clientId, clientsQuery.data?.results])

  useEffect(() => {
    if (!selectedClient) return
    setValue('diagnosis_code', selectedClient.primary_diagnosis_code)
  }, [selectedClient, setValue])

  const selectedAuthorization = useMemo(() => {
    return (authorizationsQuery.data ?? []).find((a) => a.id === authorizationId) ?? null
  }, [authorizationId, authorizationsQuery.data])

  const onSubmit = async (data: FormData) => {
    setSubmitError(null)
    if (!isOnline) {
      setSubmitError('Session creation requires an online connection.')
      return
    }

    try {
      const modifiers = (data.modifiers_csv ?? '')
        .split(',')
        .map((m) => m.trim().toUpperCase())
        .filter(Boolean)

      const scheduled_start = toIsoFromLocalInput(data.scheduled_start_local)
      const scheduled_end = toIsoFromLocalInput(data.scheduled_end_local)
      const service_date = data.scheduled_start_local.slice(0, 10)

      if (!selectedAuthorization) {
        throw new Error('Authorization is required.')
      }

      const payload = {
        client: data.client_id,
        authorization: data.authorization_id,
        service_date,
        service_code: selectedAuthorization.service_code,
        modifiers,
        scheduled_start,
        scheduled_end,
        rendering_provider: data.rendering_provider_id,
        supervising_provider: data.supervising_provider_id ?? null,
        place_of_service: data.place_of_service,
        diagnosis_code: data.diagnosis_code.trim().toUpperCase(),
        is_billable: data.is_billable,
      }

      const res = await apiClient.post('/api/v1/sessions/', payload)
      const newId = res.data?.id
      if (newId) {
        navigate(`/app/sessions/${newId}`)
      } else {
        navigate('/app/sessions')
      }
    } catch (err) {
      setSubmitError(getErrorMessage(err))
    }
  }

  if (!canCreate) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        You do not have permission to create sessions.
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Session</h1>
          <p className="text-sm text-gray-600">
            Overlaps are blocked for client and staff. Authorization must be active.
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => navigate('/app/sessions')}>
          Cancel
        </button>
      </div>

      {!isOnline && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Offline: session creation is disabled.
        </div>
      )}

      {submitError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="card p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="label" htmlFor="clientSearch">
              Find client
            </label>
            <input
              id="clientSearch"
              className="input mt-1"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              placeholder="Search by name or MRN"
            />
          </div>

          <div className="md:col-span-2">
            <label className="label" htmlFor="client_id">
              Client
            </label>
            <select id="client_id" className="input mt-1" {...register('client_id')}>
              <option value="">Select client</option>
              {(clientsQuery.data?.results ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.full_name} (MRN {c.medical_record_number})
                </option>
              ))}
            </select>
            {errors.client_id && <p className="mt-1 text-sm text-red-600">{errors.client_id.message}</p>}
          </div>

          <div className="md:col-span-2">
            <label className="label" htmlFor="authorization_id">
              Authorization
            </label>
            <select id="authorization_id" className="input mt-1" {...register('authorization_id')}>
              <option value="">Select authorization</option>
              {(authorizationsQuery.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.authorization_number} | {a.service_code} | {a.status} | remaining {a.available_units}
                </option>
              ))}
            </select>
            {errors.authorization_id && (
              <p className="mt-1 text-sm text-red-600">{errors.authorization_id.message}</p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="scheduled_start_local">
              Start
            </label>
            <input
              id="scheduled_start_local"
              type="datetime-local"
              className="input mt-1"
              {...register('scheduled_start_local')}
            />
            {errors.scheduled_start_local && (
              <p className="mt-1 text-sm text-red-600">{errors.scheduled_start_local.message}</p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="scheduled_end_local">
              End
            </label>
            <input
              id="scheduled_end_local"
              type="datetime-local"
              className="input mt-1"
              {...register('scheduled_end_local')}
            />
            {errors.scheduled_end_local && (
              <p className="mt-1 text-sm text-red-600">{errors.scheduled_end_local.message}</p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="place_of_service">
              Place of service
            </label>
            <select id="place_of_service" className="input mt-1" {...register('place_of_service')}>
              <option value="12">12 - Home</option>
              <option value="03">03 - School</option>
              <option value="11">11 - Office</option>
              <option value="22">22 - Outpatient hospital</option>
              <option value="99">99 - Other/community</option>
              <option value="02">02 - Telehealth</option>
            </select>
          </div>

          <div>
            <label className="label" htmlFor="modifiers_csv">
              Modifiers (comma-separated)
            </label>
            <input id="modifiers_csv" className="input mt-1" {...register('modifiers_csv')} placeholder="e.g., HM, HN" />
          </div>

          <div>
            <label className="label" htmlFor="diagnosis_code">
              Diagnosis code (ICD-10)
            </label>
            <input id="diagnosis_code" className="input mt-1" {...register('diagnosis_code')} />
            {errors.diagnosis_code && (
              <p className="mt-1 text-sm text-red-600">{errors.diagnosis_code.message}</p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="rendering_provider_id">
              Rendering provider
            </label>
            <select
              id="rendering_provider_id"
              className="input mt-1"
              {...register('rendering_provider_id')}
              disabled={user?.role === 'rbt'}
            >
              <option value="">Select provider</option>
              {(providersQuery.data?.results ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.full_name} ({p.role})
                </option>
              ))}
            </select>
            {errors.rendering_provider_id && (
              <p className="mt-1 text-sm text-red-600">{errors.rendering_provider_id.message}</p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="supervising_provider_id">
              Supervising provider (optional)
            </label>
            <select id="supervising_provider_id" className="input mt-1" {...register('supervising_provider_id')}>
              <option value="">None</option>
              {(providersQuery.data?.results ?? []).filter((p) => p.role === 'bcba' || p.role === 'admin').map((p) => (
                <option key={p.id} value={p.id}>
                  {p.full_name} ({p.role})
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" {...register('is_billable')} />
              Billable
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={() => navigate('/app/sessions')}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={isSubmitting || !isOnline}>
            {isSubmitting ? 'Saving...' : 'Create session'}
          </button>
        </div>
      </form>
    </div>
  )
}
